// ─────────────────────────────────────────────────────────────────────────────
// MarketingKit, the shared design system for Wavelength's public pages
// (landing, features, solutions, pricing, security, FAQ, legal, contact…).
//
// The visual language is a direct study of nor.ma: a monochrome palette on
// white, one inset full-bleed rounded hero panel with a floating pill nav over
// it, left-aligned section headings that end in a period, oversized card radii
// (24-36px), hairline #0A0A0A/10 borders, fully-rounded pill buttons, and
// generous 96-128px section rhythm. No accent color, no gradients, no glow.
//
// Type scale (matches the reference site's Suisse setup, rendered in Switzer):
//   display  56/1.02  600  -0.03em     section  36/1.10  600  -0.015em
//   card     22/1.35  600               body     16/1.6   400  #6B6B6B
//
// Photography and video do not exist yet, so every image or video slot renders
// as <MediaSlot>, a flat grey panel captioned with the asset that belongs there.
//
// Import the primitives you need; wrap a page in <MarketingShell>.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import WavelengthLogo from '../WavelengthLogo';
import { CountUp } from './Motion';
import CoolIcon from '../ui/CoolIcon';

// Scroll management for client-side navigation: React Router preserves scroll
// position across route changes, so a footer link (which sits at the bottom of
// the page) would otherwise land you at the bottom of the next page. On every
// navigation, scroll to the hashed section (offset handled by each section's
// scroll-mt-* class) or to the top when there's no hash.
function ScrollManager() {
    const { pathname, hash } = useLocation();
    useLayoutEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.slice(1));
            if (el) { el.scrollIntoView({ block: 'start' }); return; }
        }
        window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

// ── Tokens ───────────────────────────────────────────────────────────────────
// The public pages ask for Switzer by name rather than riding --app-font. The
// app-wide variable is deliberately set to the system stack in src/index.css
// (the Switzer line there is commented out), and flipping it would restyle the
// signed-in dashboard too. Switzer is already self-hosted and declared in
// public/assets/css/fonts.css at 400/500/600/700, which is every weight this
// kit uses, so naming it here is enough to pull it in for the marketing shell
// only. It falls back to --app-font wherever the face fails to load.
export const FONT =
    "'Switzer', var(--app-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif)";

export const COLORS = {
    ink: '#0A0A0A',
    ink2: '#6B6B6B',
    ink3: '#A1A1A1',
    inkSoft: '#3D3D3D',
    muted: '#6B6B6B',
    faint: '#A1A1A1',
    ghost: '#A1A1A1',
    line: 'rgba(10,10,10,0.10)',
    lineSoft: 'rgba(10,10,10,0.06)',
    lineInverse: 'rgba(255,255,255,0.14)',
    paper: '#FFFFFF',
    paper2: '#F5F5F5',
    paper3: '#ECECEC',
    faintFill: '#E3E3E3',
    coal: '#111111',
    coal2: '#1A1A1A',
    // Retained so older imports keep resolving; the palette is monochrome now.
    surface: '#F5F5F5',
    surfaceSoft: '#FAFAFA',
    dark: '#0A0A0A',
    darkHover: '#232323',
    panel: '#111111',
    accent: '#0A0A0A',
    accentBright: '#0A0A0A',
};

export const LINE = 'border-[rgba(10,10,10,0.10)]';

// ── Buttons: pill, 14/500 at h-10, 16/600 at h-12 ────────────────────────────
// Every button is a pill at one of exactly four heights, with horizontal padding
// only, the shared easing curve, and a 1% press. Nothing else.
const BTN_BASE =
    'inline-flex items-center justify-center rounded-full transition-[background-color,border-color,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(10,10,10,0.30)]';
const BTN_BASE_DARK =
    'inline-flex items-center justify-center rounded-full transition-[background-color,border-color,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40';

// h-10 (40px) is the default section button; h-12 (48px) the emphasised one.
export const BTN_PRIMARY =
    BTN_BASE + ' h-10 gap-1.5 bg-[#0A0A0A] px-4 text-[14px] font-medium text-white hover:bg-[#0A0A0A]/90';
export const BTN_SECONDARY =
    BTN_BASE + ' h-10 gap-1.5 border border-[rgba(10,10,10,0.10)] bg-white px-4 text-[14px] font-medium text-[#0A0A0A] hover:border-[rgba(10,10,10,0.30)]';
export const BTN_GHOST =
    BTN_BASE + ' h-10 px-4 text-[14px] font-medium text-[#6B6B6B] hover:text-[#0A0A0A]';
export const BTN_SM =
    BTN_BASE + ' h-9 gap-1.5 bg-[#0A0A0A] px-4 text-[14px] font-medium text-white hover:bg-[#0A0A0A]/90';
export const BTN_MD =
    BTN_BASE + ' h-12 gap-2.5 bg-[#0A0A0A] px-7 text-[16px] font-semibold text-white hover:bg-[#0A0A0A]/90';
export const BTN_MD_SECONDARY =
    BTN_BASE + ' h-12 gap-2.5 border border-[rgba(10,10,10,0.10)] bg-white px-6 text-[16px] font-semibold text-[#0A0A0A] hover:border-[rgba(10,10,10,0.30)]';
export const BTN_LARGE =
    BTN_BASE + ' h-14 gap-2.5 bg-[#0A0A0A] px-8 text-[17px] font-semibold text-white hover:bg-[#0A0A0A]/90';
// On a dark panel the primary inverts to white and the secondary is a glass pill.
export const BTN_ON_DARK =
    BTN_BASE_DARK + ' h-10 gap-1.5 bg-white px-4 text-[14px] font-medium text-[#0A0A0A] hover:bg-white/90';
export const BTN_ON_DARK_SOFT =
    BTN_BASE_DARK + ' h-10 gap-1.5 border border-white/30 bg-white/10 px-4 text-[14px] font-medium text-white backdrop-blur-md hover:bg-white/20';
export const BTN_ON_DARK_MD =
    BTN_BASE_DARK + ' h-12 gap-2.5 bg-white px-7 text-[16px] font-semibold text-[#0A0A0A] hover:bg-white/90';
export const BTN_ON_DARK_MD_SOFT =
    BTN_BASE_DARK + ' h-12 gap-2.5 border border-white/30 bg-white/10 px-6 text-[16px] font-semibold text-white backdrop-blur-md hover:bg-white/20';

// ── Tiny inline icon set: coolicons line glyphs, inherits currentColor ────────
// Keys are the meaning; values must be real names in coolicons-data.js, which
// CoolIcon warns about and renders nothing for when they are wrong.
const ICON_NAMES = {
    check: 'Check', scale: 'Ruler', wallet: 'Credit_Card_02', bank: 'Building_04', layers: 'Layers', shield: 'Shield_Check',
    bolt: 'Forward', pin: 'Map_Pin', chart: 'Chart_Line', boxes: 'Archive', users: 'Users', arrow: 'Arrow_Right_LG',
    plus: 'Add_Plus', minus: 'Remove_Minus', alert: 'Triangle_Warning', receipt: 'File_Document', sparkle: 'Star',
    lock: 'File_Check', clock: 'Timer', tag: 'Tag', cart: 'Shopping_Bag_01', phone: 'Phone', desktop: 'Desktop',
    printer: 'Printer', truck: 'Download', book: 'Book_Open', search: 'Search_Magnifying_Glass',
    cash: 'List_Checklist', gift: 'Gift', ticket: 'Ticket_Voucher', label: 'Label', map: 'Map',
    devices: 'Devices', settings: 'Settings', filter: 'Filter', mail: 'Mail', table: 'Table',
    up: 'Trending_Up', down: 'Trending_Down', file: 'File_Blank', folder: 'Folder', globe: 'Globe',
};
export function Icon({ name, className = 'h-5 w-5', strokeWidth = 1.6 }) {
    return <CoolIcon name={ICON_NAMES[name] || ICON_NAMES.check} strokeWidth={strokeWidth} className={className} />;
}

// ── Layout primitives ────────────────────────────────────────────────────────
// 1040px is the reference container. `wide` opens up to the inset-panel width.
export function Container({ children, className = '', wide = false }) {
    return (
        <div className={`mx-auto w-full ${wide ? 'max-w-[1280px]' : 'max-w-[1040px]'} px-6 sm:px-8 ${className}`}>
            {children}
        </div>
    );
}

export function Section({ children, id, className = '', tone = 'paper', pad = 'lg' }) {
    const bg = { paper: 'bg-white', paper2: 'bg-[#F5F5F5]', paper3: 'bg-[#FAFAFA]', none: '' }[tone] ?? 'bg-white';
    // Desktop rhythm 64 / 80 / 112 / 128, matching the reference site's airy
    // section spacing. `lg` is the default and carries most content sections.
    const p = { none: '', sm: 'py-12 sm:py-16', md: 'py-16 sm:py-20', lg: 'py-20 sm:py-28', xl: 'py-24 sm:py-32' }[pad] ?? 'py-20 sm:py-28';
    return <section id={id} className={`scroll-mt-24 ${bg} ${p} ${className}`}>{children}</section>;
}

// ── Type ─────────────────────────────────────────────────────────────────────
// Section headings are left-aligned and end in a period, the reference site's
// most recognizable typographic tic.
// Display type is fluid rather than stepped: one clamp per level, so a heading
// never jumps at a breakpoint. h1 32.5 -> 56, h2 24.25 -> 36, h3 18 -> 22.
export const T_H1 = 'text-[clamp(2rem,1.35rem+2.8vw,3.5rem)] leading-[1.02] font-semibold tracking-[-0.03em]';
export const T_H2 = 'text-[clamp(1.5rem,1.15rem+1.5vw,2.25rem)] leading-[1.1] font-semibold tracking-[-0.015em]';
export const T_H3 = 'text-[clamp(1.125rem,0.95rem+0.7vw,1.375rem)] leading-[1.15] font-semibold tracking-[-0.01em]';
// Lining, tabular numerals, applied wherever a figure is read rather than spoken.
export const NUMS = '[font-variant-numeric:lining-nums_tabular-nums]';

export function Heading({ children, className = '', as: Tag = 'h2' }) {
    return <Tag className={`${T_H2} text-[#0A0A0A] ${className}`}>{children}</Tag>;
}

export function Display({ children, className = '', as: Tag = 'h1' }) {
    return <Tag className={`${T_H1} text-[#0A0A0A] ${className}`}>{children}</Tag>;
}

export function CardTitle({ children, className = '' }) {
    return <h3 className={`${T_H3} text-[#0A0A0A] ${className}`}>{children}</h3>;
}

export function Lead({ children, className = '' }) {
    return <p className={`text-[17px] leading-[1.5] text-[#6B6B6B] ${className}`}>{children}</p>;
}

export function Body({ children, className = '' }) {
    return <p className={`text-[14px] leading-[1.6] text-[#6B6B6B] ${className}`}>{children}</p>;
}

// Emphasis inside a lead paragraph: on light the key phrase goes solid ink, on a
// dark panel it goes solid white while the surrounding copy stays at white/80.
export function Key({ children }) {
    return <span className="font-medium text-[#0A0A0A]">{children}</span>;
}

export function KeyDark({ children }) {
    return <span className="text-white">{children}</span>;
}

// Kept for older call sites: a muted run inside a heading.
export function Muted({ children }) {
    return <span className="text-[#A3A3A3]">{children}</span>;
}

export function Eyebrow({ children, className = '' }) {
    return (
        <div className={`text-[13px] font-medium tracking-[-0.01em] text-[#A1A1A1] ${className}`}>
            {children}
        </div>
    );
}

// ── Pills, chips, badges ─────────────────────────────────────────────────────
// Three states, not two: filled ink (active), paper with a hairline (inactive),
// and a dashed outline for something a plan or tier does not include.
export function Pill({ children, icon, active = false, muted = false, dark = false, className = '', as: Tag = 'span', ...rest }) {
    const tone = dark
        ? (active ? 'bg-white text-[#0A0A0A]' : muted ? 'border border-dashed border-white/25 text-white/45' : 'bg-white/10 text-white/85 hover:bg-white/15')
        : (active ? 'bg-[#0A0A0A] text-white' : muted ? 'border border-dashed border-[rgba(10,10,10,0.20)] text-[#A1A1A1]' : 'border border-[rgba(10,10,10,0.10)] bg-white text-[#6B6B6B] hover:border-[rgba(10,10,10,0.30)] hover:text-[#0A0A0A]');
    return (
        <Tag className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-medium transition-colors sm:text-[14px] ${tone} ${className}`} {...rest}>
            {icon && <Icon name={icon} className="h-[15px] w-[15px]" />}
            {children}
        </Tag>
    );
}

// Glass chip that floats over media, as on the reference hero and product panel.
export function FloatingChip({ children, icon, className = '' }) {
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full bg-black/25 px-3 py-1.5 text-[12px] font-medium text-white backdrop-blur-md ${className}`}>
            {icon && <Icon name={icon} className="h-[14px] w-[14px]" />}
            {children}
        </span>
    );
}

export function Badge({ children, tone = 'neutral' }) {
    const tones = {
        neutral: 'border-[rgba(10,10,10,0.10)] bg-white text-[#6B6B6B]',
        accent: 'border-[rgba(10,10,10,0.10)] bg-[#F5F5F5] text-[#0A0A0A]',
        dark: 'border-white/15 bg-white/10 text-white',
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium ${tones[tone]}`}>
            {children}
        </span>
    );
}

// A row of chips under a section, used on the reference site as a quiet feature list.
export function ChipRow({ items = [], dark = false, className = '' }) {
    return (
        <div className={`flex flex-wrap gap-2 ${className}`}>
            {items.map((it) => (
                typeof it === 'string'
                    ? <Pill key={it} dark={dark}>{it}</Pill>
                    : <Pill key={it.label} icon={it.icon} dark={dark} active={it.active}>{it.label}</Pill>
            ))}
        </div>
    );
}

// ── Surfaces ─────────────────────────────────────────────────────────────────
export function Card({ children, className = '', soft = false, radius = 'lg', pad = true }) {
    const r = { md: 'rounded-[20px]', lg: 'rounded-[24px]', xl: 'rounded-[28px]', xxl: 'rounded-[36px]' }[radius] ?? 'rounded-[24px]';
    return (
        <div className={`${r} ${soft ? 'bg-[#F5F5F5]' : 'border border-[rgba(10,10,10,0.10)] bg-white'} ${pad ? 'p-6 sm:p-7' : ''} ${className}`}>
            {children}
        </div>
    );
}

export function DarkPanel({ children, className = '', radius = 'rounded-[28px]' }) {
    return <div className={`relative overflow-hidden ${radius} bg-[#111111] text-white ${className}`}>{children}</div>;
}

// ── MediaSlot: the grey stand-in for photography, video and product shots ─────
// Renders a flat grey panel captioned with the asset that belongs there, so the
// page reads complete before any real media exists.
export function MediaSlot({
    label,
    note,
    kind = 'Image',
    ratio = '16 / 10',
    dark = false,
    className = '',
    minHeight,
    children,
}) {
    const surface = dark
        ? 'bg-white/[0.06] text-white/70 ring-1 ring-inset ring-white/10'
        : 'bg-[#F5F5F5] text-[#8A8A8A]';
    return (
        <div
            className={`relative flex w-full items-center justify-center overflow-hidden rounded-[24px] ${surface} ${className}`}
            style={{ aspectRatio: ratio, minHeight }}
        >
            <div className="flex max-w-[80%] flex-col items-center gap-2 px-6 py-8 text-center">
                <span className={`text-[11px] font-medium uppercase tracking-[0.16em] ${dark ? 'text-white/40' : 'text-[#A3A3A3]'}`}>
                    {kind}
                </span>
                <span className={`text-[15px] font-medium leading-[1.4] ${dark ? 'text-white/85' : 'text-[#3D3D3D]'}`}>
                    {label}
                </span>
                {note && (
                    <span className={`text-[13px] leading-[1.5] ${dark ? 'text-white/45' : 'text-[#8A8A8A]'}`}>{note}</span>
                )}
            </div>
            {children}
        </div>
    );
}

// ── Section head: left-aligned title + lead, the reference site's default ─────
export function SectionHead({ eyebrow, title, lead, actions, align = 'left', className = '', maxWidth = 'max-w-[560px]' }) {
    const centered = align === 'center';
    return (
        <div className={`flex flex-col gap-6 ${centered ? 'items-center text-center' : 'sm:flex-row sm:items-end sm:justify-between'} ${className}`}>
            <div className={`flex flex-col gap-3 ${centered ? 'items-center' : ''} ${maxWidth}`}>
                {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                <Heading>{title}</Heading>
                {lead && <Lead className={centered ? 'max-w-[600px]' : ''}>{lead}</Lead>}
            </div>
            {actions && <div className="flex flex-none flex-wrap items-center gap-2">{actions}</div>}
        </div>
    );
}

// Backwards-compatible alias used by older pages (defaults to centered there).
export function SectionIntro({ eyebrow, title, lead, align = 'center', className = '' }) {
    return <SectionHead eyebrow={eyebrow} title={title} lead={lead} align={align} className={`${className} ${align === 'center' ? 'mx-auto max-w-[680px]' : ''}`} />;
}

// ── Feature row: text beside a media panel, alternating ──────────────────────
export function FeatureRow({ eyebrow, title, body, bullets = [], media, reverse = false, cta }) {
    return (
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
            <div className={reverse ? 'lg:order-2' : ''}>
                {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
                {/* Same 36px H2 as every other section heading: the reference
                    site runs one section-heading size, not a second smaller one. */}
                <Heading>{title}</Heading>
                {body && <Lead className="mt-4 max-w-[460px]">{body}</Lead>}
                {bullets.length > 0 && (
                    <ul className="mt-6 flex flex-col gap-3">
                        {bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-3 text-[15px] leading-[1.55] text-[#3D3D3D]">
                                <span className="mt-[3px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-[#0A0A0A] text-white">
                                    <Icon name="check" className="h-3 w-3" strokeWidth={2.2} />
                                </span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {cta && <div className="mt-7 flex flex-wrap gap-2">{cta}</div>}
            </div>
            <div className={reverse ? 'lg:order-1' : ''}>{media}</div>
        </div>
    );
}

// ── Stat grid: big numbers in one bordered, divided card ─────────────────────
export function StatBand({ stats, countUp = false, cols, className = '' }) {
    const n = cols || Math.min(stats.length, 4);
    const grid = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'grid-cols-2 sm:grid-cols-4' }[n] ?? 'grid-cols-2 sm:grid-cols-4';
    return (
        <div className={`grid ${grid} overflow-hidden rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white ${className}`}>
            {stats.map((s, i) => (
                <div
                    key={i}
                    className="flex flex-col gap-2 border-[rgba(10,10,10,0.10)] px-6 py-8 [&:not(:first-child)]:border-t sm:[&:not(:first-child)]:border-l sm:[&:not(:first-child)]:border-t-0"
                >
                    <div className="text-[clamp(2.5rem,1.7rem+3.4vw,4rem)] font-semibold leading-none tracking-[-0.04em] text-[#0A0A0A] [font-variant-numeric:lining-nums_tabular-nums]">
                        {countUp ? <CountUp value={s.value} /> : s.value}
                    </div>
                    <div className="text-[14px] leading-[1.45] text-[#6B6B6B]">{s.label}</div>
                    {s.note && <div className="text-[12px] leading-[1.45] text-[#A3A3A3]">{s.note}</div>}
                </div>
            ))}
        </div>
    );
}

// A dark call-to-action strip that sits directly under a stat grid.
export function CTAStrip({ title, body, primary, secondary, className = '' }) {
    return (
        <div className={`flex flex-col gap-5 rounded-[24px] bg-[#111111] px-6 py-7 text-white sm:flex-row sm:items-center sm:justify-between sm:px-8 ${className}`}>
            <div>
                <div className="text-[20px] font-semibold tracking-[-0.015em] sm:text-[22px]">{title}</div>
                {body && <p className="mt-1.5 text-[14px] leading-[1.55] text-white/55">{body}</p>}
            </div>
            <div className="flex flex-none flex-wrap gap-2">
                {primary && <Link to={primary.to} className={BTN_ON_DARK}>{primary.label}</Link>}
                {secondary && <Link to={secondary.to} className={BTN_ON_DARK_SOFT}>{secondary.label}</Link>}
            </div>
        </div>
    );
}

// ── Closing CTA section ──────────────────────────────────────────────────────
export function CTASection({
    eyebrow,
    title,
    body,
    primary = { label: 'Book a walkthrough', to: '/contact' },
    secondary = { label: 'See the features', to: '/features' },
    media,
}) {
    return (
        <Section pad="lg">
            <Container>
                <DarkPanel className="px-6 py-16 sm:px-14 sm:py-20">
                    <div className="flex max-w-[620px] flex-col gap-5">
                        {eyebrow && <Eyebrow className="!text-white/45">{eyebrow}</Eyebrow>}
                        <h2 className={`${T_H2} text-white`}>{title}</h2>
                        {body && <p className="text-[16px] leading-[1.6] text-white/60">{body}</p>}
                        <div className="mt-3 flex flex-wrap gap-2">
                            {primary && <Link to={primary.to || '/contact'} className={BTN_ON_DARK}>{primary.label}</Link>}
                            {secondary && (
                                secondary.href
                                    ? <a href={secondary.href} className={BTN_ON_DARK_SOFT}>{secondary.label}</a>
                                    : <Link to={secondary.to || '/features'} className={BTN_ON_DARK_SOFT}>{secondary.label}</Link>
                            )}
                        </div>
                    </div>
                    {media && <div className="mt-12">{media}</div>}
                </DarkPanel>
            </Container>
        </Section>
    );
}

// ── Accordion ────────────────────────────────────────────────────────────────
export function Accordion({ items, className = '', defaultOpen = 0 }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className={`overflow-hidden rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white ${className}`}>
            {items.map((it, i) => {
                const isOpen = open === i;
                return (
                    <div key={i} className={i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}>
                        <button
                            type="button"
                            onClick={() => setOpen(isOpen ? -1 : i)}
                            className="flex w-full items-center justify-between gap-6 px-5 py-5 text-left transition-colors hover:bg-[#FAFAFA] sm:px-7"
                        >
                            <span className="text-[15px] font-medium leading-[1.45] text-[#0A0A0A] sm:text-[16px]">{it.q}</span>
                            <span className={`flex h-6 w-6 flex-none items-center justify-center text-[#8A8A8A] transition-transform duration-200 ${isOpen ? 'rotate-45' : ''}`}>
                                <Icon name="plus" className="h-4 w-4" />
                            </span>
                        </button>
                        {isOpen && (
                            <div className="px-5 pb-6 pr-12 text-[14px] leading-[1.7] text-[#6B6B6B] sm:px-7">{it.a}</div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Navigation ───────────────────────────────────────────────────────────────
const NAV = [
    { label: 'Platform', to: '/features', key: 'features', icon: 'layers' },
    { label: 'Who it is for', to: '/solutions', key: 'solutions', icon: 'users' },
    { label: 'Integrations', to: '/integrations', key: 'integrations', icon: 'bolt' },
    { label: 'Pricing', to: '/pricing', key: 'pricing', icon: 'wallet' },
];

function NavLinks({ active, dark }) {
    const base = 'flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[14px] font-medium transition-colors';
    return NAV.map((n) => {
        const on = active === n.key;
        const tone = dark
            ? (on ? 'bg-white text-[#0A0A0A]' : 'text-white/85 hover:text-white')
            : (on ? 'bg-white text-[#0A0A0A] shadow-[0_1px_2px_rgba(10,10,10,0.06)]' : 'text-[#6B6B6B] hover:text-[#0A0A0A]');
        return (
            <Link key={n.key} to={n.to} className={`${base} ${tone}`}>
                <Icon name={n.icon} className="h-[15px] w-[15px] opacity-70" />
                {n.label}
            </Link>
        );
    });
}

// variant="over": glass nav floating on top of a dark hero panel.
// variant="solid": sticky light nav for interior pages.
export function MarketingHeader({ active, variant = 'solid' }) {
    const [menu, setMenu] = useState(false);
    const over = variant === 'over';

    // The header itself has no background in either variant. Three pills float
    // independently: the logo, the nav group, and the sign-in / CTA pair. Over a
    // dark hero they are glass; on an interior page they are paper with a
    // hairline. That is the reference site's most recognizable chrome.
    const bar = over ? 'absolute inset-x-0 top-0 z-30' : 'sticky top-0 z-40 pt-4';
    const pill = over
        ? 'bg-white/15 text-white backdrop-blur-xl hover:bg-white/25'
        : 'border border-[rgba(10,10,10,0.10)] bg-white/80 text-[#0A0A0A] backdrop-blur-xl hover:border-[rgba(10,10,10,0.30)]';

    return (
        <header className={bar}>
            <Container className="flex h-16 items-center justify-between gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr]">
                <Link
                    to="/overview"
                    className={`flex h-10 items-center gap-2 justify-self-start rounded-full px-4 transition-[background-color,border-color] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] ${pill}`}
                    aria-label="Wavelength home"
                >
                    <WavelengthLogo className="h-[17px] w-[17px]" />
                    <span className="text-[15px] font-semibold tracking-[-0.01em]">Wavelength</span>
                </Link>

                <nav className={`hidden h-10 items-center gap-0.5 justify-self-center rounded-full p-1 backdrop-blur-xl lg:flex ${over ? 'bg-white/15' : 'border border-[rgba(10,10,10,0.10)] bg-white/80'}`}>
                    <NavLinks active={active} dark={over} />
                </nav>

                <div className="flex items-center gap-2 justify-self-end">
                    <Link
                        to="/login"
                        className={`hidden h-10 items-center rounded-full px-4 text-[14px] font-medium transition-[background-color,border-color] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] sm:inline-flex ${pill}`}
                    >
                        Sign in
                    </Link>
                    <Link
                        to="/signup"
                        className={`inline-flex h-10 items-center rounded-full px-4 text-[14px] font-medium transition-[background-color,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.99] ${over ? 'bg-white text-[#0A0A0A] hover:bg-white/90' : 'bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90'}`}
                    >
                        Start free
                    </Link>
                    <button
                        type="button"
                        onClick={() => setMenu((v) => !v)}
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors lg:hidden ${pill}`}
                        aria-label="Menu"
                    >
                        <CoolIcon name="Menu_Duo_LG" strokeWidth={1.6} className="h-5 w-5" />
                    </button>
                </div>
            </Container>

            {menu && (
                <div className="mx-6 mt-1 rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white p-2 shadow-[0_24px_60px_-24px_rgba(10,10,10,0.35)] lg:hidden">
                    <div className="flex flex-col">
                        {NAV.map((n) => (
                            <Link
                                key={n.key}
                                to={n.to}
                                onClick={() => setMenu(false)}
                                className={`rounded-[14px] px-4 py-3 text-[15px] ${active === n.key ? 'bg-[#F5F5F5] font-medium text-[#0A0A0A]' : 'text-[#6B6B6B]'}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                        <Link to="/security" onClick={() => setMenu(false)} className="rounded-[14px] px-4 py-3 text-[15px] text-[#6B6B6B]">Security</Link>
                        <Link to="/login" onClick={() => setMenu(false)} className="rounded-[14px] px-4 py-3 text-[15px] text-[#6B6B6B]">Sign in</Link>
                    </div>
                </div>
            )}
        </header>
    );
}

// ── Hero: the inset, full-bleed rounded panel with the nav floating on it ─────
export function HeroPanel({
    active,
    announcement,
    titleTop,
    titleBottom,
    body,
    primary = { label: 'Start free', to: '/signup' },
    secondary = { label: 'See the platform', to: '/features' },
    footNote,
    mediaLabel = 'Hero video',
    mediaNote,
    chips = [],
}) {
    // Inset on all four sides. The bottom padding matters as much as the others:
    // without it the panel's rounded bottom corners sit flush on the next section
    // and the hero reads as cut off rather than as a panel.
    return (
        <div className="p-3 sm:p-4">
            <div className="relative flex min-h-[600px] flex-col overflow-hidden rounded-[28px] bg-[#111111] lg:min-h-[84vh]">
                {/* The hero art is not shot yet. A flat near-black field reads as a
                    void rather than as a placeholder, so the stand-in is drawn as an
                    explicit media frame: a lighter ground, a hairline inset rule and
                    a bordered caption block. It occupies the whole panel the way the
                    real footage will, and it is obvious that art is missing rather
                    than that the hero failed to load. */}
                <div className="absolute inset-0 bg-[#1F1F1F]">
                    {/* Hairline frame, inset far enough to clear the nav and the
                        headline block so it never crowds the copy. */}
                    <div className="absolute inset-x-5 inset-y-5 rounded-[20px] ring-1 ring-inset ring-white/[0.07] sm:inset-x-8 sm:inset-y-8" />
                    <div className="absolute inset-x-0 top-[38%] flex justify-center px-6">
                        <div className="flex max-w-[520px] flex-col items-center gap-2.5 rounded-[18px] border border-dashed border-white/15 bg-white/[0.03] px-7 py-6 text-center backdrop-blur-[2px]">
                            <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/40">Video goes here</span>
                            <span className="text-[15px] font-medium leading-[1.45] text-white/70">{mediaLabel}</span>
                            {mediaNote && <span className="text-[13px] leading-[1.5] text-white/40">{mediaNote}</span>}
                        </div>
                    </div>
                </div>

                <MarketingHeader active={active} variant="over" />

                <div className="relative flex flex-1 flex-col pb-12 pt-24 sm:pt-28">
                    {/* The announcement rides at the top, directly under the nav,
                        the way the reference site does it. That anchors the upper
                        edge so the middle of the panel reads as deliberate image
                        space rather than as a void above the headline. */}
                    {announcement && (
                        <Container>
                            <FloatingChip className="hidden sm:inline-flex">{announcement}</FloatingChip>
                        </Container>
                    )}

                    <div className="flex-1" />

                    {/* Plain Container, matching the nav above it, so the headline
                        and the logo pill share a left edge. At 1280 that puts the
                        text ~148px in from the viewport edge, which is where the
                        reference site sets its hero copy. Headline lines are kept
                        short enough to survive this width at 56px. */}
                    <Container>
                        {/* Two-tone headline: the setup line recedes to white/55, the
                            payoff line lands in solid white. */}
                        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1.4fr_0.8fr] lg:gap-10">
                            <h1 className={`${T_H1} text-balance text-white`}>
                                <span className="text-white/55">{titleTop}</span>
                                {titleBottom && <><br />{titleBottom}</>}
                            </h1>
                            <div className="flex flex-col gap-6">
                                <p className="max-w-[44ch] text-[17px] font-medium leading-[1.5] text-white/80">{body}</p>
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <Link
                                        to={primary.to}
                                        className="inline-flex h-9 items-center justify-center rounded-full bg-white px-4 text-[14px] font-medium text-[#0A0A0A] transition-[background-color,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.99] hover:bg-white/90 max-sm:w-full"
                                    >
                                        {primary.label}
                                    </Link>
                                    {secondary && (
                                        <Link
                                            to={secondary.to}
                                            className="inline-flex h-9 items-center justify-center rounded-full border border-white/30 bg-white/10 px-4 text-[14px] font-medium text-white backdrop-blur-md transition-[background-color,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.99] hover:bg-white/20 max-sm:w-full"
                                        >
                                            {secondary.label}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                        {chips.length > 0 && (
                            <div className="mt-10 hidden flex-wrap gap-2 sm:flex">
                                {chips.map((c) => <FloatingChip key={c}>{c}</FloatingChip>)}
                            </div>
                        )}
                    </Container>
                </div>

                {footNote && (
                    <div className="relative flex justify-center pb-8">
                        <span className="inline-flex items-center gap-3 rounded-full bg-white/15 px-5 py-3 text-[14px] font-medium text-white ring-1 ring-white/25 backdrop-blur-md sm:text-[15px]">
                            <span className="relative flex h-2.5 w-2.5 flex-none">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-white/70 motion-safe:animate-ping" />
                                <span className="relative inline-flex h-2.5 w-2.5 shrink-0 rounded-full bg-white" />
                            </span>
                            {footNote}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

// A slab that slides up under the hero panel, keeping only its bottom corners
// rounded, so the two read as one continuous body rather than two stacked cards.
// This is the reference layout's most distinctive structural move.
// ── Interior page hero: light, left-aligned, no media panel ───────────────────
export function PageHero({ eyebrow, title, lead, primary, secondary, align = 'left', children }) {
    const centered = align === 'center';
    return (
        <Container className={`pb-10 pt-16 sm:pb-14 sm:pt-24 ${centered ? 'text-center' : ''}`}>
            <div className={`flex max-w-[720px] flex-col gap-5 ${centered ? 'mx-auto items-center' : 'items-start'}`}>
                {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                <Display>{title}</Display>
                {lead && <Lead className={`text-[17px] ${centered ? 'max-w-[600px]' : 'max-w-[560px]'}`}>{lead}</Lead>}
                {(primary || secondary) && (
                    <div className={`mt-2 flex flex-wrap gap-2 ${centered ? 'justify-center' : ''}`}>
                        {primary && <Link to={primary.to || '/contact'} className={BTN_PRIMARY}>{primary.label}</Link>}
                        {secondary && <Link to={secondary.to || '/features'} className={BTN_SECONDARY}>{secondary.label}</Link>}
                    </div>
                )}
            </div>
            {children && <div className="mt-12 sm:mt-14">{children}</div>}
        </Container>
    );
}

// ── Card grids ───────────────────────────────────────────────────────────────
// The reference site's signature 3-up: oversized radius, hairline border, grey
// media block on top, title + body underneath.
export function MediaCard({ title, body, mediaLabel, mediaNote, mediaKind = 'Image', ratio = '4 / 3', footer, step }) {
    return (
        <div className="group flex h-full flex-col overflow-hidden rounded-[36px] border border-[rgba(10,10,10,0.10)] bg-white transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-[rgba(10,10,10,0.30)]">
            <div className="flex flex-col gap-2.5 p-7">
                {step != null && (
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[rgba(10,10,10,0.10)] text-[13px] font-semibold text-[#6B6B6B] transition-colors group-hover:border-transparent group-hover:bg-[#0A0A0A] group-hover:text-white ${NUMS}`}>
                        {step}
                    </span>
                )}
                <CardTitle>{title}</CardTitle>
                <Body>{body}</Body>
                {footer}
            </div>
            {/* The picture sits at the foot of the card, under a hairline, and the
                card's own radius clips it. */}
            <div className="mt-auto min-h-[240px] flex-1 border-t border-[rgba(10,10,10,0.10)]">
                <MediaSlot label={mediaLabel} note={mediaNote} kind={mediaKind} ratio={ratio} className="!rounded-none h-full" />
            </div>
        </div>
    );
}

export function MediaCardGrid({ items, cols = 3, className = '', numbered = false }) {
    const grid = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[cols] ?? 'sm:grid-cols-2 lg:grid-cols-3';
    return (
        <div className={`grid grid-cols-1 gap-5 ${grid} ${className}`}>
            {items.map((it, i) => <MediaCard key={it.title} step={numbered ? String(i + 1).padStart(2, '0') : undefined} {...it} />)}
        </div>
    );
}

// Text-only feature cards: number or glyph, title, body.
export function FeatureGrid({ items, cols = 3, numbered = false, className = '' }) {
    const grid = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[cols] ?? 'sm:grid-cols-2 lg:grid-cols-3';
    return (
        <div className={`grid grid-cols-1 gap-4 ${grid} ${className}`}>
            {items.map((it, i) => (
                <div key={it.title} className="flex flex-col gap-3 rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white p-6">
                    {numbered ? (
                        <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3]">{String(i + 1).padStart(2, '0')}</span>
                    ) : (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-[#0A0A0A]">
                            <Icon name={it.icon || 'check'} className="h-[18px] w-[18px]" />
                        </span>
                    )}
                    <div className="text-[16px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">{it.title}</div>
                    <Body>{it.body}</Body>
                    {it.tag && <div className="mt-auto pt-2"><Badge>{it.tag}</Badge></div>}
                </div>
            ))}
        </div>
    );
}

// A bordered card whose rows are a plain list, the reference site's quiet spec table.
export function SpecList({ rows, className = '' }) {
    return (
        <div className={`overflow-hidden rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white ${className}`}>
            {rows.map((r, i) => (
                <div key={r.label} className={`flex items-baseline justify-between gap-6 px-6 py-4 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}`}>
                    <span className="text-[14px] text-[#6B6B6B]">{r.label}</span>
                    <span className="text-right text-[14px] font-medium text-[#0A0A0A]">{r.value}</span>
                </div>
            ))}
        </div>
    );
}

// ── Quiet trust row: text word-marks, no fake logos ──────────────────────────
export function LogoCloud({ label = 'Built for multi-location retail and food operators', logos = [] }) {
    return (
        <div className="flex flex-col items-center gap-5 py-2 text-center">
            <span className="text-[12px] font-medium uppercase tracking-[0.14em] text-[#A3A3A3]">{label}</span>
            {logos.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[15px] font-medium text-[#C4C4C4]">
                    {logos.map((l) => <span key={l}>{l}</span>)}
                </div>
            )}
        </div>
    );
}

// ── Quote card ───────────────────────────────────────────────────────────────
export function Quote({ quote, name, role, className = '' }) {
    return (
        <div className={`flex h-full flex-col justify-between gap-6 p-7 ${className}`}>
            <p className="text-[16px] leading-[1.6] text-[#0A0A0A]">{quote}</p>
            <div>
                <div className="text-[13px] font-medium text-[#0A0A0A]">{name}</div>
                <div className="text-[13px] text-[#8A8A8A]">{role}</div>
            </div>
        </div>
    );
}

// Three quotes inside one bordered, divided card.
export function QuoteRow({ quotes, className = '' }) {
    return (
        <div className={`grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white sm:grid-cols-3 ${className}`}>
            {quotes.map((q, i) => (
                <div key={q.name} className={`border-[rgba(10,10,10,0.08)] ${i > 0 ? 'border-t sm:border-l sm:border-t-0' : ''}`}>
                    <Quote {...q} />
                </div>
            ))}
        </div>
    );
}

// ── Numbered steps ───────────────────────────────────────────────────────────
export function Steps({ items }) {
    return (
        <div className="overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white">
            {items.map((it, i) => (
                <div key={it.title} className={`flex flex-col gap-3 px-6 py-6 sm:flex-row sm:gap-8 sm:px-8 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}`}>
                    <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3] sm:w-10 sm:flex-none sm:pt-1">{String(i + 1).padStart(2, '0')}</span>
                    <div className="max-w-[620px]">
                        <div className="text-[17px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">{it.title}</div>
                        <Body className="mt-1.5">{it.body}</Body>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── Status primitives (used by /status) ──────────────────────────────────────
const STATUS_META = {
    operational: { label: 'Operational', dot: 'bg-[#0A0A0A]', text: 'text-[#0A0A0A]', bg: 'bg-[#F5F5F5]' },
    degraded: { label: 'Degraded', dot: 'bg-[#8A8A8A]', text: 'text-[#6B6B6B]', bg: 'bg-[#F5F5F5]' },
    maintenance: { label: 'Maintenance', dot: 'bg-[#C4C4C4]', text: 'text-[#6B6B6B]', bg: 'bg-[#F5F5F5]' },
    outage: { label: 'Outage', dot: 'bg-[#0A0A0A]', text: 'text-[#0A0A0A]', bg: 'bg-[#EDEDED]' },
};
export function StatusPill({ state = 'operational', label }) {
    const m = STATUS_META[state] || STATUS_META.operational;
    return (
        <span className={`inline-flex items-center gap-2 rounded-full border border-[rgba(10,10,10,0.10)] px-3 py-1 text-[12px] font-medium ${m.bg} ${m.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
            {label || m.label}
        </span>
    );
}

export function UptimeBar({ days = 90, incidents = {} }) {
    const cells = Array.from({ length: days }, (_, i) => incidents[i] || 'operational');
    const color = { operational: 'bg-[#0A0A0A]', degraded: 'bg-[#A3A3A3]', outage: 'bg-[#D4D4D4]' };
    return (
        <div className="flex items-end gap-[2px]">
            {cells.map((c, i) => <span key={i} title={`Day ${days - i}`} className={`h-7 w-full max-w-[6px] flex-1 rounded-[2px] ${color[c] || color.operational}`} />)}
        </div>
    );
}

// Kept for older imports: a small square holding an Icon.
export function GlyphTile({ name, dark = false }) {
    return (
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${dark ? 'bg-white/10 text-white' : 'bg-[#F5F5F5] text-[#0A0A0A]'}`}>
            <Icon name={name} className="h-[18px] w-[18px]" />
        </div>
    );
}

// ── Footer ───────────────────────────────────────────────────────────────────
const FOOTER_COLS = [
    {
        title: 'Platform',
        links: [
            { label: 'Everything in Wavelength', to: '/features' },
            { label: 'How it works', to: '/how-it-works' },
            { label: 'Integrations', to: '/integrations' },
            { label: 'Pricing', to: '/pricing' },
            { label: 'Security', to: '/security' },
            { label: 'Changelog', to: '/changelog' },
        ],
    },
    {
        title: 'Who it is for',
        links: [
            { label: 'Multi-location retail', to: '/solutions#retail' },
            { label: 'Grocery and specialty food', to: '/solutions#grocery' },
            { label: 'Butcher and production', to: '/solutions#production' },
            { label: 'Wholesale and B2B', to: '/solutions#wholesale' },
            { label: 'Online and in store', to: '/solutions#omnichannel' },
        ],
    },
    {
        title: 'Compare',
        links: [
            { label: 'Back office for Toast', to: '/solutions#toast' },
            { label: 'Back office for Clover', to: '/solutions#clover' },
            { label: 'Back office for Square', to: '/solutions#square' },
            { label: 'Works with your POS', to: '/integrations' },
            { label: 'Frequent questions', to: '/faq' },
        ],
    },
    {
        title: 'Company',
        links: [
            { label: 'About', to: '/about' },
            { label: 'Customers', to: '/customers' },
            { label: 'Careers', to: '/careers' },
            { label: 'Journal', to: '/blog' },
            { label: 'Talk to us', to: '/contact' },
            { label: 'System status', to: '/status' },
        ],
    },
    {
        title: 'Legal',
        links: [
            { label: 'Privacy notice', to: '/privacy#privacy' },
            { label: 'Terms of service', to: '/terms#terms' },
            { label: 'Cookie notice', to: '/privacy#cookies' },
            { label: 'Data processing', to: '/dpa' },
        ],
    },
];

export function MarketingFooter() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    return (
        <footer className="border-t border-[rgba(10,10,10,0.08)] bg-white">
            <Container className="py-16 sm:py-20">
                <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-[1.4fr_repeat(5,1fr)]">
                    <div className="col-span-2 sm:col-span-3 lg:col-span-1">
                        <Link to="/overview" className="flex items-center gap-2 text-[#0A0A0A]">
                            <WavelengthLogo className="h-[18px] w-[18px]" />
                            <span className="text-[15px] font-semibold tracking-[-0.01em]">Wavelength</span>
                        </Link>
                        <p className="mt-3 max-w-[240px] text-[13px] leading-[1.6] text-[#8A8A8A]">
                            The back office for operators who run more than one location.
                        </p>
                        <form
                            className="mt-6 flex w-full max-w-[320px] items-center gap-2 rounded-full border border-[rgba(10,10,10,0.12)] bg-white py-1.5 pl-5 pr-1.5"
                            onSubmit={(e) => { e.preventDefault(); if (email) setSent(true); }}
                        >
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@company.com"
                                aria-label="Email address"
                                className="min-w-0 flex-1 bg-transparent text-[14px] text-[#0A0A0A] outline-none placeholder:text-[#A3A3A3]"
                            />
                            <button type="submit" className="inline-flex h-8 flex-none items-center rounded-full bg-[#0A0A0A] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[#232323]">
                                {sent ? 'Thanks' : 'Join'}
                            </button>
                        </form>
                        <p className="mt-2.5 text-[12px] text-[#A3A3A3]">Product notes every few weeks. No noise.</p>
                    </div>
                    {FOOTER_COLS.map((col) => (
                        <div key={col.title}>
                            <div className="text-[13px] font-medium text-[#0A0A0A]">{col.title}</div>
                            <ul className="mt-4 flex flex-col gap-2.5">
                                {col.links.map((l) => (
                                    <li key={l.label}>
                                        <Link to={l.to} className="text-[13px] leading-[1.5] text-[#8A8A8A] transition-colors hover:text-[#0A0A0A]">{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
                <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-[rgba(10,10,10,0.08)] pt-6 sm:flex-row sm:items-center">
                    <p className="text-[12px] text-[#A3A3A3]">© {new Date().getFullYear()} Wavelength ERP. All rights reserved.</p>
                    <div className="flex items-center gap-5 text-[12px] text-[#A3A3A3]">
                        <Link to="/status" className="flex items-center gap-1.5 transition-colors hover:text-[#0A0A0A]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#0A0A0A]" />
                            All systems operational
                        </Link>
                        <Link to="/privacy" className="transition-colors hover:text-[#0A0A0A]">Privacy</Link>
                        <Link to="/terms" className="transition-colors hover:text-[#0A0A0A]">Terms</Link>
                    </div>
                </div>
            </Container>
        </footer>
    );
}

// ── SEO head manager ─────────────────────────────────────────────────────────
// No react-helmet in the tree, so we manage the document head imperatively. Each
// marketing page renders <Seo …/> once; it sets the title, description, canonical,
// Open Graph / Twitter tags and an optional JSON-LD block, and cleans up on
// unmount. This is the standard SPA approach, the next page always sets its own.
export const SITE_URL = 'https://wavelengtherp.com';
export const SITE_NAME = 'Wavelength';

export function Seo({ title, description, path, image, type = 'website', keywords, jsonLd }) {
    const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : '';
    useLayoutEffect(() => {
        const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} · Back office and reconciliation for multi-location retail`;
        const url = SITE_URL + (path || (typeof window !== 'undefined' ? window.location.pathname : ''));
        const img = image || `${SITE_URL}/og-image.png`;
        const prevTitle = document.title;
        document.title = fullTitle;

        const created = [];
        const upsert = (sel, make, apply) => {
            let el = document.head.querySelector(sel);
            if (!el) { el = make(); document.head.appendChild(el); created.push(el); }
            apply(el);
        };
        const meta = (attr, key, content) => {
            if (!content) return;
            upsert(`meta[${attr}="${key}"]`, () => { const m = document.createElement('meta'); m.setAttribute(attr, key); return m; }, (m) => m.setAttribute('content', content));
        };
        meta('name', 'description', description);
        meta('name', 'keywords', keywords);
        meta('property', 'og:title', fullTitle);
        meta('property', 'og:description', description);
        meta('property', 'og:type', type);
        meta('property', 'og:url', url);
        meta('property', 'og:image', img);
        meta('property', 'og:site_name', SITE_NAME);
        meta('name', 'twitter:card', 'summary_large_image');
        meta('name', 'twitter:title', fullTitle);
        meta('name', 'twitter:description', description);
        meta('name', 'twitter:image', img);
        upsert('link[rel="canonical"]', () => { const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l; }, (l) => l.setAttribute('href', url));

        let script = null;
        if (jsonLdStr) { script = document.createElement('script'); script.type = 'application/ld+json'; script.text = jsonLdStr; document.head.appendChild(script); }

        return () => {
            document.title = prevTitle;
            created.forEach((el) => el.remove());
            if (script) script.remove();
        };
    }, [title, description, path, image, type, keywords, jsonLdStr]);
    return null;
}

// ── Page shell ───────────────────────────────────────────────────────────────
// `hero` pages render their own <HeroPanel> (which carries the floating nav), so
// the shell skips the solid header for them.
export function MarketingShell({ active, children, hero = false }) {
    return (
        <div className="min-h-screen w-full bg-white text-[#0A0A0A] antialiased" style={{ fontFamily: FONT }}>
            <ScrollManager />
            {!hero && <MarketingHeader active={active} />}
            <main>{children}</main>
            <MarketingFooter />
        </div>
    );
}
